import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { festivalInfo, heroEyebrows, heroSlides } from "@/mocks/festival";
import { scrollToSection } from "@/hooks/useReveal";

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const navigate = useNavigate();
  const total = heroSlides.length;

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => setIndex((v) => (v + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [playing, total]);

  return (
    <section
      id="hero"
      className="relative w-full h-[600px] md:h-screen md:max-h-[960px] md:min-h-[680px] overflow-hidden bg-primary-950"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.seq}
          className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.src}
            alt={slide.alt}
            width={1600}
            height={900}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 via-primary-950/50 to-primary-950/85"></div>

      <div className="pointer-events-none absolute -left-20 top-28 h-72 w-72 rounded-full border border-white/10 animate-floaty-slow"></div>
      <div className="pointer-events-none absolute right-[-5%] bottom-[14%] h-80 w-80 rounded-full bg-accent-500/20 blur-3xl"></div>
      <div className="pointer-events-none absolute right-[12%] top-[18%] h-16 w-16 rounded-full border border-accent-300/40 animate-floaty"></div>

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-8 text-center text-white">
        <div className="flex flex-wrap justify-center gap-2 mb-6 animate-fade-up">
          {heroEyebrows.map((item) => (
            <span
              key={item}
              className="rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs md:text-sm backdrop-blur-sm"
            >
              {item}
            </span>
          ))}
        </div>

        <p className="text-[11px] md:text-sm tracking-[0.32em] text-accent-300 animate-fade-up">
          2026 CHEONAN IPJANG GRAPE FESTIVAL
        </p>

        <h1 className="mt-4 font-heading font-black text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.15] animate-fade-up">
          제27회 입장거봉포도축제
        </h1>

        <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-white/80 animate-fade-up">
          천안 입장면이 자랑하는 대표 명품 거봉포도,
          <br className="hidden sm:block" />
          달콤한 수확의 계절을 온 가족이 함께 즐겨보세요.
        </p>

        <div className="mt-10 w-full max-w-3xl grid grid-cols-1 sm:grid-cols-3 gap-px rounded-lg overflow-hidden border border-white/20 bg-white/15 backdrop-blur-sm animate-fade-up">
          <div className="bg-primary-950/30 px-5 py-5">
            <span className="block text-[11px] tracking-[0.2em] text-accent-300">행사 일정</span>
            <strong className="mt-2 block font-heading text-lg md:text-xl">{festivalInfo.dateShort}</strong>
            <span className="mt-1 block text-xs text-white/70">{festivalInfo.timeText}</span>
          </div>
          <div className="bg-primary-950/30 px-5 py-5">
            <span className="block text-[11px] tracking-[0.2em] text-accent-300">축제 장소</span>
            <strong className="mt-2 block font-heading text-lg md:text-xl">{festivalInfo.venue}</strong>
            <span className="mt-1 block text-xs text-white/70">{festivalInfo.address}</span>
          </div>
          <div className="bg-primary-950/30 px-5 py-5">
            <span className="block text-[11px] tracking-[0.2em] text-accent-300">주최 · 주관</span>
            <strong className="mt-2 block font-heading text-lg md:text-xl">입장거봉포도축제추진위원회</strong>
            <span className="mt-1 block text-xs text-white/70">관람 무료 · 문의 {festivalInfo.tel}</span>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 animate-fade-up">
          <button
            type="button"
            onClick={() => navigate("/apply")}
            className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full btn-gradient px-7 py-3.5 text-sm md:text-base font-medium text-white"
          >
            거봉포도 수확체험 신청
            <i className="ri-arrow-right-line text-base"></i>
          </button>
          <button
            type="button"
            onClick={() => scrollToSection("schedule")}
            className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm md:text-base text-white backdrop-blur-sm hover:bg-white/20 transition-colors"
          >
            <i className="ri-calendar-line text-base"></i>
            축제 일정 보기
          </button>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 w-full z-10 px-4 md:px-8 flex items-center justify-center gap-4">
        <button
          type="button"
          aria-label={playing ? "배경 일시정지" : "배경 재생"}
          onClick={() => setPlaying((v) => !v)}
          className="cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
        >
          <i className={playing ? "ri-pause-fill" : "ri-play-fill"}></i>
        </button>
        <div className="flex items-center gap-2">
          {heroSlides.map((slide, i) => (
            <button
              key={slide.seq}
              type="button"
              aria-label={`${i + 1}번째 배경 보기`}
              onClick={() => setIndex(i)}
              className={`cursor-pointer h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-accent-400" : "w-2 bg-white/45 hover:bg-white/70"
              }`}
            ></button>
          ))}
        </div>
        <span className="text-xs text-white/70 tabular-nums">
          <span className="text-white">{String(index + 1).padStart(2, "0")}</span> / {String(total).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
}